import React from 'react';
import { Helmet } from 'react-helmet-async';

// Site-wide constants
const SITE_NAME = 'Amidex';
const SITE_URL = 'https://amidex.org';
const DEFAULT_IMAGE = '/amidex-logo.png';
const TWITTER_HANDLE = '@amidex';

/**
 * SEOHead - Comprehensive SEO meta tags component
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description (150-160 chars recommended)
 * @param {string} props.canonical - Canonical URL for the page
 * @param {string} props.ogType - Open Graph type (default: website)
 * @param {string} props.ogImage - Open Graph image URL
 * @param {Object} props.structuredData - JSON-LD structured data object
 * @param {boolean} props.noindex - Whether to noindex the page
 * @param {Array} props.breadcrumbs - Breadcrumb items for schema
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  structuredData,
  noindex = false,
  breadcrumbs,
  keywords,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Research-Grade Amino Acid Derivatives`;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  // Generate breadcrumb schema if breadcrumbs provided
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': `${SITE_URL}${item.url}`,
    })),
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO Tags */}
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Structured Data - Main */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Structured Data - Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

// Site-wide constants
const SITE_URL_CONST = 'https://amidex.org';

// Organization Schema - Site-wide (Enhanced for E-E-A-T)
export const OrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL_CONST}/#organization`,
  'name': 'Amidex',
  'url': SITE_URL_CONST,
  'logo': {
    '@type': 'ImageObject',
    'url': DEFAULT_IMAGE,
    'width': '200',
    'height': '60'
  },
  'description': 'Premium research-grade amino acid derivatives for scientific research. Third-party tested, Technical Data Sheet included with every order. Institutional Laboratory Use Only.',
  'foundingDate': '2024',
  'address': {
    '@type': 'PostalAddress',
    'addressCountry': 'US',
  },
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'customer service',
    'email': 'support@amidex.org',
    'availableLanguage': ['English']
  },
  'sameAs': [],
  'brand': {
    '@type': 'Brand',
    'name': 'Amidex',
    'slogan': 'Research-Grade Amino Acid Derivatives for Scientific Discovery'
  },
  'areaServed': {
    '@type': 'Country',
    'name': 'United States'
  },
  'knowsAbout': [
    'Amino Acid Derivative Synthesis',
    'Research Chemicals', 
    'Laboratory Supplies',
    'Biomedical Research'
  ]
};

// WebSite Schema with SearchAction
export const WebSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL_CONST}/#website`,
  'name': 'Amidex',
  'url': SITE_URL_CONST,
  'description': 'Research-grade amino acid derivatives for scientific research. Premium quality, standardized.',
  'publisher': {
    '@id': `${SITE_URL_CONST}/#organization`
  },
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${SITE_URL_CONST}/products?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  'inLanguage': 'en-US'
};

/**
 * Generate Product Schema (Enhanced with RUO compliance)
 */
export const generateProductSchema = (product) => {
  const slug = generateSlug(product.name);
  const priceValidUntil = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${SITE_URL_CONST}/products/${slug}#product`,
    'name': product.name,
    'description': product.description,
    'image': product.image_url,
    'sku': product.sku || product.id,
    'mpn': product.batch_number || '',
    'brand': {
      '@type': 'Brand',
      'name': 'Amidex',
    },
    'manufacturer': {
      '@type': 'Organization',
      'name': 'Amidex',
      '@id': `${SITE_URL_CONST}/#organization`
    },
    'category': product.category,
    'offers': {
      '@type': 'Offer',
      'url': `${SITE_URL_CONST}/products/${slug}`,
      'priceCurrency': 'USD',
      'price': product.price,
      'priceValidUntil': priceValidUntil,
      'availability': product.in_stock !== false
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      'itemCondition': 'https://schema.org/NewCondition',
      'seller': {
        '@type': 'Organization',
        'name': 'Amidex',
        '@id': `${SITE_URL_CONST}/#organization`
      },
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingDestination': {
          '@type': 'DefinedRegion',
          'addressCountry': 'US'
        },
        'deliveryTime': {
          '@type': 'ShippingDeliveryTime',
          'handlingTime': {
            '@type': 'QuantitativeValue',
            'minValue': 1,
            'maxValue': 2,
            'unitCode': 'd'
          },
          'transitTime': {
            '@type': 'QuantitativeValue',
            'minValue': 2,
            'maxValue': 5,
            'unitCode': 'd'
          }
        }
      }
    },
    'additionalProperty': [
      {
        '@type': 'PropertyValue',
        'name': 'Purity',
        'value': product.purity || '≥99%',
      },
      {
        '@type': 'PropertyValue',
        'name': 'Dosage',
        'value': product.dosage,
      },
      {
        '@type': 'PropertyValue',
        'name': 'Intended Use',
        'value': 'Institutional Laboratory Use Only (RUO)',
      },
      {
        '@type': 'PropertyValue',
        'name': 'Storage',
        'value': product.storage || 'Store at -20°C'
      }
    ],
  };
};

/**
 * Generate Research Article Schema for amino acid derivative pages
 */
export const generateResearchArticleSchema = (peptide, studyCount = 0) => {
  const today = new Date().toISOString().split('T')[0];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${SITE_URL_CONST}/portal/peptide/${peptide.slug}#article`,
    'headline': `${peptide.name} Research Profile & Scientific Documentation`,
    'description': peptide.overview?.substring(0, 200) + '...' || '',
    'image': peptide.image_url || '',
    'datePublished': peptide.created_at?.split('T')[0] || today,
    'dateModified': peptide.updated_at?.split('T')[0] || today,
    'author': {
      '@type': 'Organization',
      'name': 'Amidex Research Team',
      '@id': `${SITE_URL_CONST}/#organization`
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Amidex',
      '@id': `${SITE_URL_CONST}/#organization`,
      'logo': {
        '@type': 'ImageObject',
        'url': DEFAULT_IMAGE
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${SITE_URL_CONST}/portal/peptide/${peptide.slug}`
    },
    'about': {
      '@type': 'ChemicalSubstance',
      'name': peptide.name,
      'description': peptide.overview || ''
    },
    'keywords': [peptide.name, 'amino acid derivative research', 'research amino acid derivative', peptide.category || ''],
    'articleSection': 'Research',
    'wordCount': peptide.overview?.split(' ').length || 0,
    'citation': `${studyCount} referenced studies`
  };
};

/**
 * Generate FAQ Schema
 */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map((faq) => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer,
    },
  })),
});

/**
 * Generate Breadcrumb Schema
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': `${SITE_URL_CONST}${item.url}`,
  })),
});

/**
 * Generate URL-friendly slug from product name
 */
export const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special chars except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

/**
 * Get product ID from slug (reverse lookup needed in backend)
 */
export const getIdFromSlug = (slug) => {
  // This would typically call an API, but for now we'll handle it in the component
  return slug;
};

/**
 * Generate Author Schema for expert profiles
 */
export const generateAuthorSchema = (author) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${SITE_URL_CONST}/about/team/${author.slug || author.id}#person`,
  'name': author.name,
  'jobTitle': author.title || author.role,
  'description': author.bio,
  'image': author.image_url,
  'url': `${SITE_URL_CONST}/about/team/${author.slug || author.id}`,
  'worksFor': {
    '@type': 'Organization',
    'name': 'Amidex',
    '@id': `${SITE_URL_CONST}/#organization`
  },
  'sameAs': [
    author.linkedin_url,
    author.twitter_url,
    author.orcid_url
  ].filter(Boolean),
  'knowsAbout': author.expertise || ['Amino Acid Derivative Research', 'Biochemistry'],
  'alumniOf': author.education ? {
    '@type': 'EducationalOrganization',
    'name': author.education
  } : undefined
});

/**
 * Generate Review Schema for product reviews
 */
export const generateReviewSchema = (review, productName) => ({
  '@context': 'https://schema.org',
  '@type': 'Review',
  'reviewRating': {
    '@type': 'Rating',
    'ratingValue': review.rating,
    'bestRating': 5,
    'worstRating': 1
  },
  'author': {
    '@type': review.author_type === 'organization' ? 'Organization' : 'Person',
    'name': review.author_name
  },
  'reviewBody': review.content,
  'datePublished': review.date,
  'itemReviewed': {
    '@type': 'Product',
    'name': productName
  }
});

/**
 * Generate AggregateRating Schema for products
 */
export const generateAggregateRatingSchema = (product) => {
  if (!product.rating_count || product.rating_count < 1) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateRating',
    'ratingValue': product.average_rating?.toFixed(1) || '5.0',
    'reviewCount': product.rating_count || 0,
    'bestRating': 5,
    'worstRating': 1,
    'itemReviewed': {
      '@type': 'Product',
      'name': product.name,
      'url': `${SITE_URL_CONST}/products/${product.slug || product.id}`
    }
  };
};

/**
 * Generate MedicalWebPage Schema for research content
 */
export const generateMedicalWebPageSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalWebPage',
  'name': page.title,
  'description': page.description,
  'url': `${SITE_URL_CONST}${page.url}`,
  'lastReviewed': page.reviewed_date || new Date().toISOString().split('T')[0],
  'reviewedBy': {
    '@type': 'Person',
    'name': page.reviewer_name || 'Amidex Research Team',
    'jobTitle': page.reviewer_title || 'Scientific Advisor'
  },
  'medicalAudience': {
    '@type': 'MedicalAudience',
    'audienceType': 'Researcher'
  },
  'specialty': {
    '@type': 'MedicalSpecialty',
    'name': 'Biochemistry'
  }
});

/**
 * Amidex Expert Authors for content attribution
 */
export const EXPERT_AUTHORS = {
  research_team: {
    id: 'research-team',
    name: 'Amidex Research Team',
    title: 'Scientific Research Division',
    bio: 'Our team of biochemists and researchers specializes in amino acid derivative science, conducting rigorous analysis and documentation of research compounds.',
    image_url: DEFAULT_IMAGE,
    expertise: ['Amino Acid Derivative Research', 'Biochemistry', 'Quality Control', 'Research Documentation']
  },
  dr_chen: {
    id: 'dr-sarah-chen',
    name: 'Dr. Sarah Chen, Ph.D.',
    title: 'Chief Scientific Officer',
    bio: 'Dr. Chen leads our research initiatives with over 15 years of experience in amino acid derivative biochemistry and pharmaceutical research.',
    image_url: DEFAULT_IMAGE,
    expertise: ['Amino Acid Derivative Synthesis', 'Drug Development', 'Clinical Research'],
    education: 'Stanford University'
  },
  dr_martinez: {
    id: 'dr-michael-martinez',
    name: 'Dr. Michael Martinez, Ph.D.',
    title: 'Senior Research Scientist',
    bio: 'Dr. Martinez specializes in amino acid derivative pharmacology and has published extensively on research compound applications.',
    image_url: DEFAULT_IMAGE,
    expertise: ['Pharmacology', 'Amino Acid Derivative Biology', 'Research Protocols'],
    education: 'MIT'
  }
};

export default SEOHead;
